import { jsxs, jsx } from 'react/jsx-runtime';
import { isPresetGradientColor, isPresetColor, colors } from '@flex-design-system/fx-colors';
import { forwardRef, useId } from 'react';
import { getId, FlexLinearGradient } from './LinearGradient.mjs';
import { useLogoSize } from './useLogoSize.mjs';

const FlexLogo = forwardRef(
  ({
    color: colorFromProps = "currentColor",
    size: sizeFromProps = "medium",
    variant = "combination",
    style: styleFromProps,
    ...props
  }, forwardedRef) => {
    const gradientId = useId();
    const { width, height } = useLogoSize({ size: sizeFromProps, variant });
    const isGradientColor = isPresetGradientColor(colorFromProps);
    const color = isGradientColor ? `url(#${getId(colorFromProps, gradientId)})` : isPresetColor(colorFromProps) ? colors[colorFromProps] : colorFromProps;
    const style = {
      width,
      height,
      flexShrink: 0,
      ...styleFromProps
    };
    switch (variant) {
      case "combination": {
        return /* @__PURE__ */ jsxs(
          "svg",
          {
            width,
            height,
            viewBox: "0 0 94 28",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            style,
            ref: forwardedRef,
            ...props,
            children: [
              isGradientColor && /* @__PURE__ */ jsx(FlexLinearGradient, { id: gradientId }),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M7.3047 1.49106e-06C8.03106 0.00050052 8.75593 0.111445 9.45494 0.327364H9.45344C11.3136 0.904476 12.837 2.17213 13.746 3.89501C13.7755 3.94909 13.8029 4.00838 13.8295 4.06568L13.8385 4.08506L13.8499 4.10944C12.7873 5.19053 11.9888 6.52634 11.5385 7.97957C11.2281 8.98156 11.0883 10.0343 11.1216 11.1079C11.0748 11.1064 11.0261 11.1064 10.9773 11.1064C9.55842 11.1054 8.12758 11.4323 6.83803 12.0512C5.87286 12.5129 4.99227 13.1353 4.21963 13.9034C2.76243 13.2293 1.59527 12.1208 0.843528 10.6915C-0.0654238 8.96663 -0.245523 6.99251 0.331092 5.13431C0.799249 3.61989 1.7276 2.32586 3.01417 1.39403C4.25695 0.494528 5.78032 -0.000992012 7.3047 1.49106e-06Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M56.1463 1.36666H51.7513V26.0835H56.1463V1.36666Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M44.8971 5.76167H48.8464V1.36666H44.8971C41.3349 1.36666 38.5449 4.20297 38.5449 7.82335V9.81688H36.7006V13.6905H38.5449V26.0835H42.9399V13.6905H48.8469V9.81688H42.9399V7.82335C42.9399 6.64773 43.7812 5.76167 44.8971 5.76167Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M92.5243 9.79299L86.6686 17.587L93.0521 26.0835H88.1472L88.1248 26.0536L84.291 20.7442H84.1407L80.2845 26.0835H75.3796L81.7631 17.587L75.9074 9.79299H80.8124L80.8348 9.82284L84.1427 14.4323H84.2885L87.6193 9.79299H92.5243Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  clipRule: "evenodd",
                  d: "M70.9433 21.2298C70.1627 22.378 68.9756 22.9467 67.309 22.9467C64.9179 22.9467 63.2901 21.6636 62.8289 19.4218H75.5965L75.6318 19.2233C75.699 18.8472 75.7288 18.4422 75.7288 17.9104C75.7288 12.891 72.1099 9.24822 67.1239 9.24822C62.1379 9.24822 58.5185 12.9179 58.5185 17.9741C58.5185 23.0303 62.1896 26.6681 67.2473 26.6681C71.0866 26.6681 74.0368 24.6999 75.3408 21.2681L75.3557 21.2293L70.9433 21.2298ZM67.0926 12.8671C69.2473 12.8671 70.8 14.096 71.3672 16.1452H62.8204C63.398 14.1547 65.0264 12.8671 67.0926 12.8671Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M17.8604 19.987C16.2857 19.4994 14.8579 18.6064 13.731 17.4054C12.6823 16.2875 11.9062 14.9149 11.4838 13.4303C11.2957 13.4174 11.13 13.4104 10.9763 13.4104C9.88826 13.4099 8.82906 13.6507 7.83105 14.1273C6.07434 14.9681 4.74997 16.4432 4.10022 18.2805C3.45197 20.1178 3.55794 22.0984 4.39873 23.8556C5.24101 25.6143 6.71613 26.9387 8.54996 27.5855C12.3425 28.9193 16.5146 26.9283 17.8549 23.1352C18.1992 22.1621 18.3265 21.1372 18.236 20.091C18.1255 20.0631 18.0027 20.0308 17.8808 19.9935L17.8604 19.987Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M20.7131 3.52735C21.4415 3.52784 22.1663 3.63879 22.8633 3.85471C24.725 4.43331 26.2504 5.69898 27.1559 7.42385C28.0633 9.14524 28.2469 11.1194 27.6718 12.981C27.0932 14.8392 25.824 16.3631 24.1011 17.2721C22.3763 18.1825 20.3992 18.3666 18.5445 17.788C16.6877 17.2114 15.1644 15.9437 14.2554 14.2174C13.3465 12.4925 13.1644 10.5199 13.738 8.66166C14.2091 7.14524 15.1375 5.85321 16.4226 4.92137C17.6653 4.02187 19.1887 3.52635 20.7131 3.52735Z",
                  fill: color
                }
              )
            ]
          }
        );
      }
      case "symbol": {
        return /* @__PURE__ */ jsxs(
          "svg",
          {
            width,
            height,
            viewBox: "0 0 28 28",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            style,
            ref: forwardedRef,
            ...props,
            children: [
              isGradientColor && /* @__PURE__ */ jsx(FlexLinearGradient, { id: gradientId }),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M7.3047 1.49106e-06C8.03106 0.00050052 8.75593 0.111445 9.45494 0.327364H9.45344C11.3136 0.904476 12.837 2.17213 13.746 3.89501C13.7755 3.94909 13.8029 4.00838 13.8295 4.06568L13.8385 4.08506L13.8499 4.10944C12.7873 5.19053 11.9888 6.52634 11.5385 7.97957C11.2281 8.98156 11.0883 10.0343 11.1216 11.1079C11.0748 11.1064 11.0261 11.1064 10.9773 11.1064C9.55842 11.1054 8.12758 11.4323 6.83803 12.0512C5.87286 12.5129 4.99227 13.1353 4.21963 13.9034C2.76243 13.2293 1.59527 12.1208 0.843528 10.6915C-0.0654238 8.96663 -0.245523 6.99251 0.331092 5.13431C0.799249 3.61989 1.7276 2.32586 3.01417 1.39403C4.25695 0.494528 5.78032 -0.000992012 7.3047 1.49106e-06Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M17.8604 19.987C16.2857 19.4994 14.8579 18.6064 13.731 17.4054C12.6823 16.2875 11.9062 14.9149 11.4838 13.4303C11.2957 13.4174 11.13 13.4104 10.9763 13.4104C9.88826 13.4099 8.82906 13.6507 7.83105 14.1273C6.07434 14.9681 4.74997 16.4432 4.10022 18.2805C3.45197 20.1178 3.55794 22.0984 4.39873 23.8556C5.24101 25.6143 6.71613 26.9387 8.54996 27.5855C12.3425 28.9193 16.5146 26.9283 17.8549 23.1352C18.1992 22.1621 18.3265 21.1372 18.236 20.091C18.1255 20.0631 18.0027 20.0308 17.8808 19.9935L17.8604 19.987Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M20.7131 3.52735C21.4415 3.52784 22.1663 3.63879 22.8633 3.85471C24.725 4.43331 26.2504 5.69898 27.1559 7.42385C28.0633 9.14524 28.2469 11.1194 27.6718 12.981C27.0932 14.8392 25.824 16.3631 24.1011 17.2721C22.3763 18.1825 20.3992 18.3666 18.5445 17.788C16.6877 17.2114 15.1644 15.9437 14.2554 14.2174C13.3465 12.4925 13.1644 10.5199 13.738 8.66166C14.2091 7.14524 15.1375 5.85321 16.4226 4.92137C17.6653 4.02187 19.1887 3.52635 20.7131 3.52735Z",
                  fill: color
                }
              )
            ]
          }
        );
      }
      case "text": {
        return /* @__PURE__ */ jsxs(
          "svg",
          {
            width,
            height,
            viewBox: "0 0 57 26",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            style,
            ref: forwardedRef,
            ...props,
            children: [
              isGradientColor && /* @__PURE__ */ jsx(FlexLinearGradient, { id: gradientId }),
              /* @__PURE__ */ jsx("path", { d: "M19.4457 0H15.0507V24.7168H19.4457V0Z", fill: color }),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M8.19648 4.39501H12.1457V3.8147e-06H8.19648C4.63431 3.8147e-06 1.84427 2.83631 1.84427 6.45669V8.45022H0V12.3238H1.84427V24.7168H6.23928V12.3238H12.1462V8.45022H6.23928V6.45669C6.23928 5.28108 7.08057 4.39501 8.19648 4.39501Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M55.8237 8.42634L49.968 16.2203L56.3515 24.7168H51.4466L51.4242 24.687L47.5904 19.3775H47.4401L43.5839 24.7168H38.6789L45.0625 16.2203L39.2068 8.42634H44.1118L44.1341 8.45619L47.4421 13.0656H47.5879L50.9187 8.42634H55.8237Z",
                  fill: color
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  fillRule: "evenodd",
                  clipRule: "evenodd",
                  d: "M34.2427 19.8631C33.4621 21.0114 32.275 21.58 30.6083 21.58C28.2173 21.58 26.5895 20.2969 26.1283 18.0552H38.8959L38.9312 17.8566C38.9984 17.4805 39.0282 17.0756 39.0282 16.5437C39.0282 11.5243 35.4093 7.88157 30.4233 7.88157C25.4372 7.88157 21.8178 11.5512 21.8178 16.6074C21.8178 21.6636 25.489 25.3014 30.5466 25.3014C34.3859 25.3014 37.3362 23.3332 38.6401 19.9014L38.6551 19.8626L34.2427 19.8631ZM30.3919 11.5005C32.5466 11.5005 34.0994 12.7293 34.6665 14.7786H26.1198C26.6974 12.788 28.3258 11.5005 30.3919 11.5005Z",
                  fill: color
                }
              )
            ]
          }
        );
      }
      default: {
        throw new Error(
          `[FlexLogo] \uC815\uC758\uB418\uC9C0 \uC54A\uC740 variant\uC5D0\uC694. 'combination', 'symbol', 'text' \uC911 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.`
        );
      }
    }
  }
);

export { FlexLogo };
//# sourceMappingURL=index.mjs.map
